import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_5612de96-9817-4dde-8f5e-f254b2b066d6/artifacts/5ie5ityv_Untitled%20%283000%20x%20800%20px%29.png";

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Company Profile", to: "/company-profile" },
  { label: "Practice Areas", to: "/services" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const onScroll = useCallback(() => setScrolled(window.scrollY > 12), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-paper/95 backdrop-blur-md border-b border-rule" : "bg-paper border-b border-transparent"
      }`}
    >
      {/* Top utility strip */}
      <div className="hidden md:block bg-brand text-white text-[11px] tracking-[0.25em] uppercase">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-9">
          <span data-testid="header-tagline">Advocates &amp; Solicitors · New Delhi</span>
          <div className="flex items-center gap-6">
            <a href="tel:919643572870" className="link-underline" data-testid="header-phone">
              +91 96435 72870
            </a>
            <a href="mailto:info@kandkassociates.in" className="link-underline" data-testid="header-email">
              info@kandkassociates.in
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={LOGO_URL} alt="K & K Associates" className="h-12 md:h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] xl:text-[12px] tracking-[0.2em] xl:tracking-[0.25em] uppercase font-medium whitespace-nowrap transition-colors ${
                  isActive ? "text-brand" : "text-ink hover:text-brand"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to="/contact" className="btn-primary !py-3 !px-6" data-testid="header-cta-consultation">
            <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Book Consultation
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 -mr-2 text-ink"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-rule bg-paper" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-5">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `text-base tracking-wider uppercase font-medium py-1 ${
                    isActive ? "text-brand" : "text-ink"
                  }`
                }
                data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {it.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary mt-4" data-testid="mobile-cta-consultation">
              Book Consultation
            </Link>
            <a href="tel:919643572870" className="text-sm text-ink/70" data-testid="mobile-phone">
              Call: +91 96435 72870
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
